import { useCallback, useEffect, useRef, useState } from "react";
import { AxiosError } from "axios";
import { searchPatientsV2Api } from "../Apis/opportunities";
import {
  fetchPatientFromHisByUhidApi,
  searchPatientsWithHisApi,
  type Patient,
  type PatientHisPreviewRecord,
  type PatientHisSearchRecord,
  type PatientHisSearchSource,
} from "../Apis/patients";
import type {
  SearchLeadsHisSearchMetadata,
  SearchLeadsResultItem,
} from "../Interfaces/OpportunitiesInterface";
import { isSearchQueryReady } from "../constants/searchConstraints";

export type UniversalSearchResultSource =
  "leads" | "his_preview" | "his_local" | null;

export type UniversalSearchEmptyReason =
  "his_ineligible" | "not_found" | "his_disabled" | "his_unavailable";

const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_PAGE_LIMIT = 50;

/** One HIS fallback lookup (by phone or by UHID), normalised. */
interface HisLookupResult {
  source: PatientHisSearchSource;
  local: PatientHisSearchRecord[];
  preview: PatientHisPreviewRecord[];
}

/** CAR-2947: the backend answers 502 HIS_UNAVAILABLE / 504 HIS_TIMEOUT when HIS can't be reached. */
function isHisUnavailableError(err: unknown): boolean {
  const response = (
    err as { response?: { status?: number; data?: { error?: unknown } } }
  )?.response;
  const code = response?.data?.error;
  return (
    response?.status === 502 ||
    response?.status === 504 ||
    code === "HIS_UNAVAILABLE" ||
    code === "HIS_TIMEOUT"
  );
}

function toHisLocalRecord(patient: Patient): PatientHisSearchRecord {
  return {
    id: patient.id,
    tenantId: patient.tenantId,
    uhid: patient.uhid,
    primaryPhone: patient.primaryPhone ?? "",
    fullName: patient.fullName,
    firstName: patient.firstName,
    lastName: patient.lastName,
    age: patient.age,
    gender: patient.gender as PatientHisSearchRecord["gender"],
    dateOfBirth: patient.dateOfBirth,
    email: patient.email,
    city: patient.city,
    state: patient.state,
    primaryFacilityId: patient.primaryFacilityId,
    isVip: Boolean(patient.isVip),
    doNotCall: Boolean(patient.doNotCall),
    doNotMessage: Boolean(patient.doNotMessage),
    createdAt: patient.createdAt ?? "",
    updatedAt: patient.updatedAt ?? "",
  };
}

async function hisPhoneLookup(phone: string): Promise<HisLookupResult> {
  const response = await searchPatientsWithHisApi(phone, { preview: true });
  const source = response?.data?.source ?? "not_found";
  return {
    source,
    local: source === "local" ? (response?.data?.patients ?? []) : [],
    preview: response?.data?.hisPreview ?? [],
  };
}

async function hisUhidLookup(uhid: string): Promise<HisLookupResult> {
  const response = await fetchPatientFromHisByUhidApi(uhid, undefined, {
    preview: true,
  });
  const source = response?.data?.source ?? "not_found";
  const patient = response?.data?.patient;
  return {
    source,
    local: source === "local" && patient ? [toHisLocalRecord(patient)] : [],
    preview: response?.data?.hisPreview ?? [],
  };
}

/**
 * CAR-2947: which HIS lookups a search hint asks for. A UHID goes to the UHID
 * lookup, a phone to MagicSearch, and a number that could be either to both.
 * Older backends send no `kind` — treat their hint as phone-only, as before.
 */
function hisLookupsFor(
  hint: SearchLeadsHisSearchMetadata
): Array<() => Promise<HisLookupResult>> {
  const kind = hint.kind ?? (hint.phone ? "phone" : null);
  const { phone, uhid } = hint;
  const lookups: Array<() => Promise<HisLookupResult>> = [];
  if ((kind === "phone" || kind === "ambiguous") && phone) {
    lookups.push(() => hisPhoneLookup(phone));
  }
  if ((kind === "uhid" || kind === "ambiguous") && uhid) {
    lookups.push(() => hisUhidLookup(uhid));
  }
  return lookups;
}

function dedupeBy<T>(items: T[], key: (item: T) => string): T[] {
  const seen = new Set<string>();
  return items.filter((item) => {
    const k = key(item);
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

function parseSearchAxiosError(err: unknown): string | null {
  if (!(err instanceof AxiosError)) {
    return "Search failed. Please try again.";
  }

  const status = err.response?.status;
  const data = err.response?.data as Record<string, unknown> | undefined;
  const nestedError = data?.error as Record<string, unknown> | undefined;
  const code =
    (nestedError?.code as string | undefined) ??
    (data?.code as string | undefined) ??
    (data?.errorCode as string | undefined);
  const message =
    (nestedError?.message as string | undefined) ??
    (data?.message as string | undefined) ??
    (data?.error as string | undefined);

  if (
    status === 400 &&
    (code === "INVALID_PHONE" || code === "MISSING_PHONE")
  ) {
    return null;
  }

  if (status === 403) {
    return "You don't have permission to search patients for this tenant.";
  }

  if (status === 501) {
    return "Patient search is not available on this deployment.";
  }

  if (typeof message === "string" && message.trim()) {
    return message.trim();
  }

  return "Search failed. Please try again.";
}

export interface UseUniversalSearchQueryOptions {
  enabled: boolean;
  searchQuery: string;
}

export interface UseUniversalSearchQueryResult {
  searchResults: SearchLeadsResultItem[];
  hisPreviewResults: PatientHisPreviewRecord[];
  hisLocalResults: PatientHisSearchRecord[];
  resultSource: UniversalSearchResultSource;
  emptyReason: UniversalSearchEmptyReason | null;
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  hasMore: boolean;
  hisSearchPhone: string | null;
  loadMore: () => void;
  importHisPreviewPatient: (
    registrationNo: string,
    phone: string
  ) => Promise<string>;
  importingRegistrationNo: string | null;
  /** Re-runs the current search (e.g. after "Couldn't reach HIS"). */
  retry: () => void;
  reset: () => void;
}

export function useUniversalSearchQuery(
  options: UseUniversalSearchQueryOptions
): UseUniversalSearchQueryResult {
  const { enabled, searchQuery } = options;

  const [searchResults, setSearchResults] = useState<SearchLeadsResultItem[]>(
    []
  );
  const [hisPreviewResults, setHisPreviewResults] = useState<
    PatientHisPreviewRecord[]
  >([]);
  const [hisLocalResults, setHisLocalResults] = useState<
    PatientHisSearchRecord[]
  >([]);
  const [resultSource, setResultSource] =
    useState<UniversalSearchResultSource>(null);
  const [emptyReason, setEmptyReason] =
    useState<UniversalSearchEmptyReason | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [hisSearchPhone, setHisSearchPhone] = useState<string | null>(null);
  const [importingRegistrationNo, setImportingRegistrationNo] = useState<
    string | null
  >(null);

  const nextCursorRef = useRef<string | null>(null);
  const debounceRef = useRef<number | null>(null);
  const requestIdRef = useRef(0);

  const reset = useCallback(() => {
    setSearchResults([]);
    setHisPreviewResults([]);
    setHisLocalResults([]);
    setResultSource(null);
    setEmptyReason(null);
    setLoading(false);
    setLoadingMore(false);
    setError(null);
    setHasMore(false);
    setHisSearchPhone(null);
    setImportingRegistrationNo(null);
    nextCursorRef.current = null;
  }, []);

  const runHisFallback = useCallback(
    async (
      lookups: Array<() => Promise<HisLookupResult>>,
      requestId: number
    ) => {
      const settled = await Promise.allSettled(lookups.map((run) => run()));
      if (requestId !== requestIdRef.current) return;

      const answered = settled
        .filter(
          (r): r is PromiseFulfilledResult<HisLookupResult> =>
            r.status === "fulfilled"
        )
        .map((r) => r.value);

      const localPatients = dedupeBy(
        answered.flatMap((r) => r.local),
        (p) => p.id
      );
      if (localPatients.length > 0) {
        setHisLocalResults(localPatients);
        setHisPreviewResults([]);
        setResultSource("his_local");
        setEmptyReason(null);
        return;
      }

      const preview = dedupeBy(
        answered.flatMap((r) => r.preview),
        (p) => p.registrationNo
      );
      if (preview.length > 0) {
        setHisPreviewResults(preview);
        setHisLocalResults([]);
        setResultSource("his_preview");
        setEmptyReason(null);
        return;
      }

      // CarePro already returned no rows. A HIS failure must not block Add
      // Contact (CAR-2831): it stays an empty state, never an error banner.
      setHisPreviewResults([]);
      setHisLocalResults([]);
      setResultSource(null);
      setError(null);

      // Nothing found but a lookup couldn't reach HIS: say so (CAR-2947),
      // otherwise it reads exactly like "patient not in HIS". This covers an
      // ambiguous number whose UHID lookup answered "no match" while the phone
      // search was down — the patient may still be there.
      const unavailable = settled.some(
        (r) => r.status === "rejected" && isHisUnavailableError(r.reason)
      );
      if (unavailable) {
        setEmptyReason("his_unavailable");
        return;
      }

      setEmptyReason(
        answered.length > 0 &&
          answered.every((r) => r.source === "his_disabled")
          ? "his_disabled"
          : "not_found"
      );
    },
    []
  );

  const executeSearch = useCallback(
    async (term: string, cursor?: string) => {
      const requestId = ++requestIdRef.current;
      const isLoadMore = Boolean(cursor);

      if (isLoadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        setError(null);
        setEmptyReason(null);
        setHisPreviewResults([]);
        setHisLocalResults([]);
        setResultSource(null);
        setHisSearchPhone(null);
        nextCursorRef.current = null;
        setHasMore(false);
      }

      try {
        const response = await searchPatientsV2Api({
          searchQuery: term,
          limit: SEARCH_PAGE_LIMIT,
          cursor,
        });

        if (requestId !== requestIdRef.current) return;

        const rows = response?.data ?? [];
        const pagination = response?.pagination;
        nextCursorRef.current = pagination?.nextCursor ?? null;
        setHasMore(Boolean(pagination?.hasMore && pagination?.nextCursor));

        if (rows.length > 0) {
          setSearchResults((prev) => (isLoadMore ? [...prev, ...rows] : rows));
          setHisPreviewResults([]);
          setHisLocalResults([]);
          setResultSource("leads");
          setEmptyReason(null);
          return;
        }

        if (isLoadMore) {
          return;
        }

        setSearchResults([]);

        const hisSearch = response?.metadata?.hisSearch;
        const hisLookups = hisSearch?.eligible ? hisLookupsFor(hisSearch) : [];
        if (hisLookups.length > 0) {
          setHisSearchPhone(hisSearch?.phone ?? null);
          await runHisFallback(hisLookups, requestId);
          return;
        }

        setHisSearchPhone(null);
        setEmptyReason("his_ineligible");
      } catch (err: unknown) {
        if (requestId !== requestIdRef.current) return;

        if (isLoadMore) {
          setError(parseSearchAxiosError(err));
          return;
        }

        setSearchResults([]);
        const parsed = parseSearchAxiosError(err);
        if (parsed) {
          setError(parsed);
        }
      } finally {
        if (requestId !== requestIdRef.current) return;
        if (isLoadMore) {
          setLoadingMore(false);
        } else {
          setLoading(false);
        }
      }
    },
    [runHisFallback]
  );

  const loadMore = useCallback(() => {
    const term = searchQuery.trim();
    if (!isSearchQueryReady(term) || !hasMore || loading || loadingMore) {
      return;
    }
    const cursor = nextCursorRef.current;
    if (!cursor) return;
    void executeSearch(term, cursor);
  }, [searchQuery, hasMore, loading, loadingMore, executeSearch]);

  const retry = useCallback(() => {
    const term = searchQuery.trim();
    if (!enabled || !isSearchQueryReady(term) || loading) return;
    void executeSearch(term);
  }, [enabled, searchQuery, loading, executeSearch]);

  const importHisPreviewPatient = useCallback(
    async (registrationNo: string, phone: string): Promise<string> => {
      setImportingRegistrationNo(registrationNo);
      setError(null);
      try {
        const response = await fetchPatientFromHisByUhidApi(
          registrationNo,
          phone
        );
        const patientId = response?.data?.patient?.id;
        if (!patientId) {
          throw new Error("Patient import failed. Please try again.");
        }
        return patientId;
      } catch (err: unknown) {
        const parsed = parseSearchAxiosError(err);
        setError(
          parsed ?? "Failed to import patient from HIS. Please try again."
        );
        throw err;
      } finally {
        setImportingRegistrationNo(null);
      }
    },
    []
  );

  useEffect(() => {
    if (!enabled) {
      reset();
      return;
    }

    const term = searchQuery.trim();
    if (!term) {
      reset();
      return;
    }

    if (!isSearchQueryReady(term)) {
      reset();
      return;
    }

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = window.setTimeout(() => {
      void executeSearch(term);
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
    };
  }, [enabled, searchQuery, executeSearch, reset]);

  return {
    searchResults,
    hisPreviewResults,
    hisLocalResults,
    resultSource,
    emptyReason,
    loading,
    loadingMore,
    error,
    hasMore,
    hisSearchPhone,
    loadMore,
    importHisPreviewPatient,
    importingRegistrationNo,
    retry,
    reset,
  };
}
